/**
 * /library/songs/$jobId — view a finished song.
 *
 * Why this exists (#231):
 *   The Songs Library row's "open" button used to point at
 *   /library/song-workspace?job=<id> — which loaded the EMPTY
 *   add-a-song form because the workspace doesn't consume the ?job
 *   param. User reported: "When the song is done, where do I view,
 *   where can i see lyric and translation? when i open it goes to
 *   the youtube pasting folder."
 *
 *   This route is the missing destination. Tiny — just fetches the
 *   queue row to get the song title + metadata, then mounts the
 *   existing ArtifactPanel (audio player + script+translation
 *   side-by-side viewer with karaoke highlight + print-to-PDF) on
 *   the artifacts for this ingest_job_id.
 *
 * Why a dedicated route (not a modal):
 *   Same shareable-URL UX as the rest of the app — the user can
 *   bookmark a song, link to it from notes, hit back to return to
 *   the queue. Modals would lose that.
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ChevronLeft, ExternalLink, Loader2, Music2, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { ArtifactPanel } from "./_app.library_.song-workspace";
import { Button } from "@/components/ui/button";
// #237 — Shared origin-aware FastAPI URL resolver.
import { apiHeaders, fastapiBase } from "@/lib/fastapi-url";
import { useSession } from "@/hooks/use-session";

function apiBase(): string {
  return fastapiBase();
}

// #248c — Route ID is "/_app/library_/songs_/$jobId" (note the
// trailing underscore on `songs_`). The original "/songs/$jobId" path
// made TanStack treat this as a CHILD of the songs LIST route — which
// has no <Outlet />, so navigating to a song detail URL silently
// rendered the list page ("goes and comes back" bug). The trailing
// underscore lifts this route to be a sibling, so it renders as its
// own page at /library/songs/<id>.
export const Route = createFileRoute("/_app/library_/songs_/$jobId")({
  head: ({ params }) => ({
    meta: [{ title: `Song ${params.jobId.slice(0, 8)} — Ojaswati` }],
  }),
  component: SongDetail,
});

/* Mirror of the QueueRow shape from songs.tsx (kept inline so we
 * don't pull the whole list module in just for the type). Only the
 * fields actually rendered here are typed strictly. */
type QueueRowLite = {
  id: number;
  ingest_job_id: string | null;
  status: string;
  url: string;
  request_payload: {
    drive_folder_id?: string;
    drive_folder_name?: string;
    meta?: {
      title?: string;
      composer?: string;
      raga?: string;
      album?: string;
      year?: string;
      language?: string;
      language_iso?: string;
    };
  } | null;
};

function SongDetail() {
  const { jobId } = useParams({ from: "/_app/library_/songs_/$jobId" });
  const apiUrl = apiBase();
  const session = useSession();
  const email = session.data?.email ?? null;
  const [row, setRow] = useState<QueueRowLite | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Look the row up so we have title + folder + meta for the
  // ArtifactPanel header + Drive-upload context.
  //
  // #248c — Backend's /youtube-queue/list requires user_email (FastAPI
  // validation, returns 422 without it). Wait for the session to load
  // before firing the request so we always have an email to pass.
  useEffect(() => {
    if (!email) return;
    let abort = false;
    (async () => {
      try {
        const res = await fetch(
          `${apiUrl}/youtube-queue/list?user_email=${encodeURIComponent(email)}&limit=200`,
          { headers: apiHeaders({ Accept: "application/json" }) },
        );
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const list = (await res.json()) as QueueRowLite[];
        const match = list.find((r) => r.ingest_job_id === jobId);
        if (!abort) {
          if (match) setRow(match);
          else setError("Song not found in your queue");
        }
      } catch (e) {
        if (!abort) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      abort = true;
    };
  }, [apiUrl, email, jobId]);

  const meta = row?.request_payload?.meta ?? {};
  const songTitle = meta.title?.trim() || `Song ${jobId.slice(0, 8)}`;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Back-nav + title header — same pattern as Add a Song. */}
      <div className="flex items-center gap-3 mb-4">
        <Link
          to="/library/songs"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-2 py-1.5"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Songs
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-2xl flex items-center gap-2 truncate">
            <Music2 className="h-5 w-5 text-primary shrink-0" />
            <span className="truncate">{songTitle}</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-1 truncate">
            {[
              meta.composer,
              meta.raga,
              meta.album,
              meta.year,
              meta.language,
            ]
              .filter(Boolean)
              .join(" · ") || "Ingested from YouTube"}
          </p>
        </div>
        {row?.url && (
          <a
            href={row.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            title="Open YouTube source"
          >
            <ExternalLink className="h-3 w-3" /> YouTube
          </a>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <div className="font-medium text-destructive mb-1">
            Couldn't load the song
          </div>
          <div className="text-destructive/80 font-mono text-xs">{error}</div>
        </div>
      )}

      {!row && !error && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading song…
        </div>
      )}

      {row && (
        <ArtifactPanel
          apiUrl={apiUrl}
          jobId={jobId}
          title="Listen & review"
          driveFolderId={row.request_payload?.drive_folder_id ?? null}
          songTitle={songTitle}
          /* #263 — Pass the YouTube URL so ArtifactPanel can auto-fetch
             canonical lyrics from Gemini-grounded on mount. When the
             song is recognized, canonical sections (Pallavi / Anupallavi
             / Charanams) become the primary view; whisper-based table
             is the fallback for unrecognized songs. */
          youtubeUrl={row.url ?? undefined}
          songMeta={{
            title: songTitle,
            language: (meta.language as any) ?? "Sanskrit",
            composer: meta.composer ?? "",
            raga: meta.raga ?? "",
            album: meta.album ?? "",
            year: meta.year ?? "",
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * CanonicalLyricsPanel — fetch an LLM's canonical lyrics on demand.
 *
 * The Whisper-based viewer is great for karaoke timing but bad at
 * lyric accuracy on songs with refrain repetition or noisy audio. For
 * documented compositions, this panel asks the backend (which tries
 * DeepSeek first, falls back to Gemini #262) for the canonical
 * Pallavi/Anupallavi/Charanam lines and displays them as a separate
 * read-only reference.
 *
 * Future iterations may merge canonical text into the karaoke timing
 * — for now this is a clean side-by-side read.
 * ───────────────────────────────────────────────────────────────────── */

type CanonicalVerse = {
  section: string;
  sanskrit: string;
  transliteration: string;
  translation: string;
};

function CanonicalLyricsPanel({
  apiUrl,
  youtubeUrl,
  songTitle,
}: {
  apiUrl: string;
  youtubeUrl: string;
  songTitle: string;
}) {
  const [verses, setVerses] = useState<CanonicalVerse[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ title: string; composer: string; confidence: string } | null>(
    null,
  );

  const fetchCanonical = async () => {
    setLoading(true);
    setError(null);
    setVerses(null);
    setMeta(null);
    try {
      const res = await fetch(`${apiUrl}/songs/identify`, {
        method: "POST",
        headers: apiHeaders({
          "content-type": "application/json",
          accept: "application/json",
        }),
        body: JSON.stringify({
          url: youtubeUrl,
          title: songTitle,
          include_lyrics: true,
        }),
      });
      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}`);
      }
      const data = (await res.json()) as {
        recognized?: boolean;
        title?: string;
        composer?: string;
        confidence?: string;
        canonical_lyrics?: CanonicalVerse[];
      };
      if (!data.recognized) {
        throw new Error(
          "The LLM didn't recognize this song — no canonical lyrics available.",
        );
      }
      const lyrics = data.canonical_lyrics ?? [];
      if (lyrics.length === 0) {
        throw new Error(
          `Identified as "${data.title}" but returned no canonical lyrics ` +
            `(confidence: ${data.confidence}). For modern songs lyrics may be withheld; for ` +
            `traditional compositions this is unusual — try again or paste manually.`,
        );
      }
      setVerses(lyrics);
      setMeta({
        title: data.title ?? "",
        composer: data.composer ?? "",
        confidence: data.confidence ?? "low",
      });
      toast.success(
        `Canonical lyrics: ${lyrics.length} section${lyrics.length === 1 ? "" : "s"} found`,
        {
          description: `${data.title}${data.composer ? ` · ${data.composer}` : ""}`,
          duration: 3500,
        },
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast.error("Couldn't fetch canonical lyrics", {
        description: msg,
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mt-6 rounded-lg border border-violet-300/40 dark:border-violet-500/30 bg-violet-50/40 dark:bg-violet-500/5 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold inline-flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-300" />
            Canonical lyrics
          </h2>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Reference text fetched from an LLM (DeepSeek primary, Gemini
            fallback) for documented compositions — useful when Whisper
            transcription has gaps or repetitions.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchCanonical}
          disabled={loading}
          className="gap-1.5 border-violet-300 dark:border-violet-500/30"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          {loading ? "Asking LLM…" : verses ? "Refresh" : "Fetch canonical"}
        </Button>
      </div>

      {error && (
        <div className="rounded border border-amber-300/40 bg-amber-50/40 dark:bg-amber-500/5 p-3 text-xs text-amber-800 dark:text-amber-200 inline-flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {meta && verses && (
        <div className="text-[11px] text-muted-foreground">
          Source: <span className="font-medium">{meta.title}</span>
          {meta.composer && <> · {meta.composer}</>}
          {" · "}
          confidence <span className="font-medium">{meta.confidence}</span>
        </div>
      )}

      {verses && verses.length > 0 && (
        <div className="space-y-3">
          {verses.map((v, i) => (
            <div
              key={i}
              className="rounded border border-border bg-background p-3 space-y-2"
            >
              <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700 dark:text-violet-300">
                {v.section}
              </div>
              <div className="text-sm leading-relaxed font-serif whitespace-pre-wrap">
                {v.sanskrit}
              </div>
              {v.transliteration && (
                <div className="text-xs leading-relaxed text-muted-foreground italic whitespace-pre-wrap">
                  {v.transliteration}
                </div>
              )}
              {v.translation && (
                <div className="text-xs leading-relaxed text-foreground/90 whitespace-pre-wrap border-l-2 border-violet-200 dark:border-violet-500/30 pl-3">
                  {v.translation}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
